import {
  AppShellContent,
  Avatar,
  Button,
  Group,
  Headline,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
  Text,
} from "@assetwatch/ui";
import { useState } from "react";
import {
  WorkPage,
  WorkPageState,
  WorkPanel,
} from "../components/layout/WorkPageLayout";
import { useRetainedLocalListState } from "../components/navigation/useRetainedListState";
import { SearchAndFilters } from "../components/SearchAndFilters";
import { AddTeamModal } from "../components/snowflakes/teams-users/AddTeamModal";
import { EditTeamModal } from "../components/snowflakes/teams-users/EditTeamModal";
import { TeamPill } from "../components/snowflakes/teams-users/TeamPill";
import {
  type ColumnSort,
  nextColumnSort,
  resolveColumnSort,
  type SortDirection,
  sortTeams,
  sortUsers,
  type TeamSortField,
  teamLead,
  type UserSortField,
} from "../components/snowflakes/teams-users/teamsUsersSort";
import {
  useTeamsUsersMutations,
  type WorkRoleCode,
} from "../queries/useTeamsUsersMutations";
import {
  type TeamsUsersResponse,
  useTeamsUsersQuery,
} from "../queries/useTeamsUsersQuery";

type Team = TeamsUsersResponse["teams"][number];

const roleOptions: { value: WorkRoleCode; label: string }[] = [
  { value: "REQUESTER", label: "Requester" },
  { value: "OPERATOR", label: "Operator" },
  { value: "TECHNICIAN", label: "Technician" },
  { value: "ADMINISTRATOR", label: "Administrator" },
];
type TeamStatus = "active" | "inactive";
const teamStatusOptions: { value: TeamStatus; label: string }[] = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

type TeamsUsersListState = {
  tab: "users" | "teams";
  search: string;
  roles: WorkRoleCode[];
  teamStatuses: TeamStatus[];
  // Each tab keeps its own sort. Unset means the default order.
  userSort?: UserSortField;
  userSortDir?: SortDirection;
  teamSort?: TeamSortField;
  teamSortDir?: SortDirection;
};
const EMPTY_LIST_STATE: TeamsUsersListState = {
  tab: "users",
  search: "",
  roles: [],
  teamStatuses: [],
};
const roleCodeByName = new Map<string, WorkRoleCode>(
  roleOptions.map((option) => [option.label, option.value]),
);

function initials(name: string) {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function formatLastLogin(iso: string | null) {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  const startOfDay = (value: Date) =>
    new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();
  const dayMs = 24 * 60 * 60 * 1000;
  const days = Math.round((startOfDay(new Date()) - startOfDay(date)) / dayMs);
  const time = date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  if (days <= 0) return `Today · ${time}`;
  if (days === 1) return `Yesterday · ${time}`;
  if (days < 7) return `${days} days ago`;
  return date.toLocaleDateString();
}

export function TeamsUsersScreen() {
  const { data, error, isPending, isError } = useTeamsUsersQuery();
  const mutations = useTeamsUsersMutations();
  const [listState, setListState] =
    useRetainedLocalListState<TeamsUsersListState>(
      "teams-users",
      EMPTY_LIST_STATE,
    );
  const { search, tab } = listState;
  // A restored selection is validated against what the filters offer.
  const roles = listState.roles.filter((role) =>
    roleOptions.some((option) => option.value === role),
  );
  const teamStatuses = listState.teamStatuses.filter((status) =>
    teamStatusOptions.some((option) => option.value === status),
  );
  const setSearch = (value: string) =>
    setListState((current) => ({ ...current, search: value }));
  const setTab = (value: string) =>
    setListState((current) => ({
      ...current,
      tab: value === "teams" ? "teams" : "users",
    }));
  const setRoles = (value: WorkRoleCode[]) =>
    setListState((current) => ({ ...current, roles: value }));
  const setTeamStatuses = (value: TeamStatus[]) =>
    setListState((current) => ({ ...current, teamStatuses: value }));
  const userSort = resolveColumnSort(listState.userSort, listState.userSortDir);
  const teamSort = resolveColumnSort(listState.teamSort, listState.teamSortDir);
  const toggleUserSort = (field: UserSortField) => {
    const next = nextColumnSort(userSort, field);
    setListState((current) => ({
      ...current,
      userSort: next.field,
      userSortDir: next.dir,
    }));
  };
  const toggleTeamSort = (field: TeamSortField) => {
    const next = nextColumnSort(teamSort, field);
    setListState((current) => ({
      ...current,
      teamSort: next.field,
      teamSortDir: next.dir,
    }));
  };
  /* @ds-deviation: POC defines no sort for Teams & Users; product asked for column sorting — fix in product (POC) */
  function sortedHead<Field extends string>(
    sort: ColumnSort<Field> | undefined,
    onSort: (field: Field) => void,
    field: Field,
    label: string,
    variant?: "starting",
  ) {
    let sortDirection: "ascending" | "descending" | false = false;
    if (sort?.field === field) {
      sortDirection = sort.dir === "desc" ? "descending" : "ascending";
    }
    return (
      <TableHead
        onSort={() => onSort(field)}
        sortable
        sortDirection={sortDirection}
        variant={variant}
      >
        {label}
      </TableHead>
    );
  }
  const userHead = (
    field: UserSortField,
    label: string,
    variant?: "starting",
  ) => sortedHead(userSort, toggleUserSort, field, label, variant);
  const teamHead = (
    field: TeamSortField,
    label: string,
    variant?: "starting",
  ) => sortedHead(teamSort, toggleTeamSort, field, label, variant);
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);
  const [addTeamOpen, setAddTeamOpen] = useState(false);

  if (isPending) {
    return (
      <AppShellContent pending>
        <Text>Loading Teams and Users…</Text>
      </AppShellContent>
    );
  }

  if (isError || !data) {
    return (
      <AppShellContent>
        <WorkPage
          header={
            <Headline as="h1" size="lg">
              Teams / Users
            </Headline>
          }
        >
          <WorkPageState tone="negative">
            <Text>
              {error instanceof Error
                ? error.message
                : "Unable to load Teams and Users."}
            </Text>
          </WorkPageState>
        </WorkPage>
      </AppShellContent>
    );
  }

  const normalizedSearch = search.trim().toLowerCase();
  const users = sortUsers(
    data.users.filter(
      (user) =>
        (roles.length === 0 ||
          (user.role
            ? roles.some((role) => roleCodeByName.get(user.role ?? "") === role)
            : false)) &&
        (!normalizedSearch ||
          `${user.name} ${user.email ?? ""} ${user.role ?? ""}`
            .toLowerCase()
            .includes(normalizedSearch)),
    ),
    userSort,
  );
  const teams = sortTeams(
    data.teams.filter(
      (team) =>
        (teamStatuses.length === 0 ||
          teamStatuses.includes(team.isActive ? "active" : "inactive")) &&
        (!normalizedSearch ||
          team.name.toLowerCase().includes(normalizedSearch)),
    ),
    teamSort,
  );
  const selectedTeam: Team | null =
    data.teams.find((team) => team.id === selectedTeamId) ?? null;

  return (
    <AppShellContent>
      <WorkPage
        header={
          <Group justify="between" wrap="wrap">
            <Headline as="h1" size="lg">
              Teams / Users
            </Headline>
            {data.canManage && tab === "teams" && (
              <Button
                icon="add-plus"
                variant="primary"
                onClick={() => setAddTeamOpen(true)}
              >
                Add team
              </Button>
            )}
          </Group>
        }
        tabs={
          <Tabs value={tab} onValueChange={(value: string) => setTab(value)}>
            <TabsList>
              <TabsTrigger value="users" chipLabel={data.users.length}>
                Users
              </TabsTrigger>
              <TabsTrigger value="teams" chipLabel={data.teams.length}>
                Teams
              </TabsTrigger>
            </TabsList>
          </Tabs>
        }
        toolbar={
          tab === "users" ? (
            <SearchAndFilters
              activeFilterCount={roles.length}
              canReset={roles.length > 0 || search.trim().length > 0}
              filterAriaLabel="User filters"
              facets={[
                {
                  key: "role",
                  label: "Role",
                  options: roleOptions,
                  value: roles,
                  onChange: (values) => setRoles(values as WorkRoleCode[]),
                },
              ]}
              onSearchChange={setSearch}
              onReset={() => {
                setRoles([]);
                setSearch("");
              }}
              searchPlaceholder="Search users"
              searchValue={search}
            />
          ) : (
            <SearchAndFilters
              activeFilterCount={teamStatuses.length}
              canReset={teamStatuses.length > 0 || search.trim().length > 0}
              filterAriaLabel="Team filters"
              facets={[
                {
                  key: "status",
                  label: "Status",
                  options: teamStatusOptions,
                  value: teamStatuses,
                  onChange: (values) => setTeamStatuses(values as TeamStatus[]),
                },
              ]}
              onSearchChange={setSearch}
              onReset={() => {
                setTeamStatuses([]);
                setSearch("");
              }}
              searchPlaceholder="Search teams"
              searchValue={search}
            />
          )
        }
      >
        {tab === "users" ? (
          <WorkPanel>
            <div className="min-h-0 flex-1 overflow-auto">
              <Table aria-label="Users" size="md">
                <TableHeader>
                  <TableRow section="header">
                    {userHead("name", "Name", "starting")}
                    {userHead("role", "Role")}
                    {userHead("teams", "Team(s)")}
                    {userHead("lastLogin", "Last login")}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell variant="starting">
                        <Group gap={8} wrap="nowrap">
                          <Avatar
                            initials={initials(user.name)}
                            size="xs"
                            variant="external"
                          />
                          <Text>{user.name}</Text>
                        </Group>
                      </TableCell>
                      <TableCell>
                        {data.canManage && user.id !== data.currentUserId ? (
                          <div className="max-w-[190px]">
                            <Select
                              options={roleOptions}
                              placeholder="No role"
                              reserveSubtextSpace={false}
                              size="sm"
                              value={
                                (user.role
                                  ? roleCodeByName.get(user.role)
                                  : null) ?? null
                              }
                              onChange={(value) => {
                                if (value)
                                  mutations.setUserRole.mutate({
                                    userId: user.id,
                                    role: value as WorkRoleCode,
                                  });
                              }}
                            />
                          </div>
                        ) : (
                          (user.role ?? "—")
                        )}
                      </TableCell>
                      <TableCell>
                        {user.teams.length ? (
                          <Group gap={8} wrap="wrap">
                            {user.teams.map((team) => (
                              <TeamPill key={team} name={team} />
                            ))}
                          </Group>
                        ) : (
                          <Text tone="subtle">Unassigned</Text>
                        )}
                      </TableCell>
                      <TableCell>{formatLastLogin(user.lastLogin)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </WorkPanel>
        ) : (
          <WorkPanel>
            <div className="min-h-0 flex-1 overflow-auto">
              <Table aria-label="Teams" size="md">
                <TableHeader>
                  <TableRow section="header">
                    {teamHead("team", "Team", "starting")}
                    {teamHead("lead", "Lead")}
                    {teamHead("members", "Members")}
                    {teamHead("description", "Description")}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {teams.map((team) => {
                    const lead = teamLead(team);
                    return (
                      <TableRow
                        key={team.id}
                        interactive
                        tabIndex={0}
                        role="button"
                        aria-label={`Edit ${team.name}`}
                        onClick={() => setSelectedTeamId(team.id)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            setSelectedTeamId(team.id);
                          }
                        }}
                      >
                        <TableCell variant="starting">{team.name}</TableCell>
                        <TableCell>
                          {lead ? (
                            <Group gap={8} wrap="nowrap">
                              <Avatar
                                initials={initials(lead.name)}
                                size="xs"
                                variant="external"
                              />
                              <Text>{lead.name}</Text>
                            </Group>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="tabular-nums">
                            {team.memberCount}
                          </span>
                        </TableCell>
                        <TableCell>
                          {team.description ?? <Text tone="subtle">—</Text>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </WorkPanel>
        )}
      </WorkPage>

      <AddTeamModal
        open={addTeamOpen}
        onOpenChange={setAddTeamOpen}
        onCreate={(input) => mutations.createTeam.mutateAsync(input)}
        isSaving={mutations.createTeam.isPending}
      />

      <EditTeamModal
        team={selectedTeam}
        users={data.users}
        canManage={data.canManage}
        onClose={() => setSelectedTeamId(null)}
        onSave={(input) => mutations.replaceTeam.mutateAsync(input)}
        isSaving={mutations.replaceTeam.isPending}
      />
    </AppShellContent>
  );
}
